import { DateTime } from 'luxon';
import { LanguageService } from './language.service';
import { stringify } from 'qs';

export interface IServiceBaseError {
  httpStatusCode: number | undefined;
  errorCode: number | undefined;
  errorMessage: string | undefined;
}

export class AccessTokenApiModel {
  public token!: string;
  public expiresIn!: number;
}

export class OauthHttpClientWrapper {
  private token: string | undefined;
  private currentTokenExpires: DateTime | undefined;
  private readonly baseUrl: string;

  constructor(baseURL: string) {
    this.baseUrl = baseURL;
  }

  public getUrl(url: string, params?: URLSearchParams): string {
    const paramsString = params?.toString();
    return `${this.baseUrl}${url}${paramsString?.length ? `?${paramsString}` : ''}`;
  }

  public getHeaders(): HeadersInit {
    return {
      'Accept-Language': LanguageService.getCurrentLanguage(),
      Authorization: `Bearer ${this.token}`,
    };
  }

  public setToken(token: string, expiresIn: number): void {
    this.currentTokenExpires = DateTime.now().toUTC().plus({ seconds: expiresIn });
    this.token = token;
  }

  public getToken(): string | undefined {
    return this.token;
  }

  public hasValidToken(): boolean {
    const nowWithMargin = DateTime.now().toUTC().plus({ hours: 2 });
    return this.hasToken() && this.tokenExpires !== undefined && this.tokenExpires > nowWithMargin;
  }

  private get tokenExpires(): DateTime | undefined {
    return this.currentTokenExpires;
  }

  private hasToken(): boolean {
    return this.token !== undefined;
  }
}

export abstract class ServiceBase {
  protected static getWithTryGetNewToken<T>(httpClientWrapper: OauthHttpClientWrapper, url: string, params?: URLSearchParams): Promise<T> {
    if (httpClientWrapper.hasValidToken()) {
      return new Promise((resolve, reject) => {
        const handleGetErrorFunc: (errorResponse: Response) => void | PromiseLike<void> = async (errorResponse) => {
          const serviceBaseError = await ServiceBase.getServiceBaseError(errorResponse);
          const isGwUnauthorizedError = errorResponse && errorResponse.status === 401 && !!serviceBaseError.errorCode;
          if (!isGwUnauthorizedError) {
            reject(serviceBaseError); // Error from API, or error that is not 401 from api-gw
            return;
          }
          ServiceBase.getAndSetAuthorizationToken(httpClientWrapper, true, httpClientWrapper.getToken() ?? 'unknown', serviceBaseError.errorCode)
            .then(() => fetch(httpClientWrapper.getUrl(url, params), { headers: httpClientWrapper.getHeaders() }))
            .then((response) => (response.ok ? resolve(response.json() as T) : Promise.reject(response)))
            .catch(async (retryError: Response) => {
              reject(await ServiceBase.getServiceBaseError(retryError));
            });
        };

        fetch(httpClientWrapper.getUrl(url, params), { headers: httpClientWrapper.getHeaders() })
          .then((response) => (response.ok ? resolve(response.json()) : Promise.reject(response)))
          .catch(handleGetErrorFunc);
      });
    }

    return new Promise((resolve, reject) => {
      ServiceBase.getAndSetAuthorizationToken(httpClientWrapper, false, httpClientWrapper.getToken())
        .then(() => fetch(httpClientWrapper.getUrl(url, params), { headers: httpClientWrapper.getHeaders() }))
        .then((response) => (response.ok ? resolve(response.json() as T) : Promise.reject(response)))
        .catch(async (errorResponse: Response) => {
          reject(await ServiceBase.getServiceBaseError(errorResponse));
        });
    });
  }

  private static tokenPromise: Promise<void> | undefined;

  private static async getServiceBaseError(errorResponse: Response): Promise<IServiceBaseError> {
    try {
      const responseJson = (await errorResponse.json()) as { errorCode: number; errorMessage: string; fault: { code: number; message: string } };
      return {
        httpStatusCode: errorResponse.status,
        errorCode: responseJson?.fault?.code ?? responseJson.errorCode,
        errorMessage: responseJson?.fault?.message ?? responseJson.errorMessage,
      };
    } catch {
      return {
        httpStatusCode: errorResponse?.status,
        errorCode: undefined,
        errorMessage: undefined,
      };
    }
  }

  private static getAndSetAuthorizationToken(
    httpClientWrapper: OauthHttpClientWrapper,
    unauthorized: boolean,
    expiredToken: string | undefined,
    faultCode: number | undefined = undefined
  ): Promise<void> {
    if (!this.tokenPromise) {
      this.tokenPromise = new Promise((resolve, reject) => {
        let endpoint = '';
        if (unauthorized) {
          endpoint = 'expired';
        } else {
          endpoint = expiredToken ? 'renew' : 'new';
        }
        const query = new URLSearchParams(stringify({ expiredToken, faultCode }));
        fetch(`/api/token/external/${endpoint}${query.size ? `?${query}` : ''}`)
          .then((response) => response.json())
          .then((tokenResponse) => {
            httpClientWrapper.setToken(tokenResponse.token, tokenResponse.expiresIn);
            ServiceBase.tokenPromise = undefined;
            resolve();
          })
          .catch(async (errorResponse: Response) => {
            ServiceBase.tokenPromise = undefined;
            reject(await ServiceBase.getServiceBaseError(errorResponse));
          });
      });
    }
    return this.tokenPromise;
  }
}
